<template>
  <span :class="badgeClasses">
    <slot />
  </span>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  /**
   * Badge variant/status
   * @values success, warning, error, info, default, paid, pending, overdue
   */
  variant: {
    type: String,
    default: 'default',
    validator: (value) =>
      ['success', 'warning', 'error', 'info', 'default',
       'paid', 'pending', 'overdue',
       'draft', 'finalized', 'cancelled', 'partial'].includes(value),
  },
  /**
   * Badge size
   * @values sm, md
   */
  size: {
    type: String,
    default: 'md',
    validator: (value) => ['sm', 'md', 'lg'].includes(value),
  },
})

// Map invoice status names to variants
const variantMap = {
  paid: 'success',
  overdue: 'error',
  finalized: 'success',
  draft: 'default',
  cancelled: 'error',
  partial: 'warning',
}

const normalizedVariant = computed(() => variantMap[props.variant] || props.variant)

const badgeClasses = computed(() => [
  'intercom-badge',
  `intercom-badge--${normalizedVariant.value}`,
  `intercom-badge--${props.size}`,
])
</script>

<style scoped>
/* ========================================
   INTERCOM BADGE BASE (Pill-shaped)
   ======================================== */
.intercom-badge {
  display: inline-block;
  font-family: var(--font-family);
  font-weight: var(--font-weight-semibold);
  border-radius: var(--radius-pill);
  white-space: nowrap;
}

/* ========================================
   SIZES
   ======================================== */
.intercom-badge--sm {
  padding: 2px 8px;
  font-size: var(--font-size-xs);
}

.intercom-badge--md {
  padding: 4px 12px;
  font-size: var(--font-size-sm);
}

.intercom-badge--lg {
  padding: 6px 16px;
  font-size: var(--font-size-base);
}

/* ========================================
   VARIANTS
   ======================================== */
.intercom-badge--success {
  background: var(--color-success-bg);
  color: var(--color-success);
}

.intercom-badge--warning {
  background: var(--color-warning-bg);
  color: var(--color-warning);
}

.intercom-badge--error {
  background: var(--color-error-bg);
  color: var(--color-error);
}

.intercom-badge--info {
  background: var(--color-info-bg);
  color: var(--color-info);
}

.intercom-badge--default {
  background: var(--color-gray-100);
  color: var(--color-gray-700);
}

.intercom-badge--pending {
  background: var(--color-pending-bg);
  color: var(--color-pending);
}
</style>
